import * as React from 'react'

import { deriveContentState } from './deriveContentState'
import { deriveTabs } from './deriveTabs'
import { useGlobalSearchNavigation } from '../../../../hooks/useGlobalSearchNavigation'
import { type GlobalSearchRecentItem } from '../../../../value-objects/GlobalSearchRecentItem'
import { type GlobalSearchResultItem } from '../../../../value-objects/GlobalSearchResultItem'
import { type ContentStateEnum } from '../../enums/ContentStateEnum'
import {
  GlobalSearchTabTypes,
  type GlobalSearchTab,
  type GlobalSearchTabTypeEnum,
} from '../../enums/GlobalSearchTabTypeEnum'
import { useMutateAddGlobalSearchRecentItem } from '../../models/useMutateAddGlobalSearchRecentItem'
import { useQueryGlobalSearchRecentItems } from '../../models/useQueryGlobalSearchRecentItems'
import { useQueryGlobalSearchResults } from '../../models/useQueryGlobalSearchResults'

interface IUseGetGlobalSearchResultsPresenterResult {
  commands: {
    onQueryChange: (value: string) => void
    onSubmit: () => void
    onRecentItemClick: (item: GlobalSearchRecentItem) => void
    onResultItemClick: (item: GlobalSearchResultItem) => void
    onTabChange: (type: GlobalSearchTabTypeEnum) => void
  }
  data: {
    query: string
    recentItems: GlobalSearchRecentItem[]
    resultItems: GlobalSearchResultItem[]
    tabs: Nullable<GlobalSearchTab[]>
    inputRef: React.RefObject<HTMLInputElement>
  }
  states: {
    showEnterHint: boolean
    contentState: Nullable<ContentStateEnum>
    selectedTab: GlobalSearchTabTypeEnum
    showTruncatedNotice: boolean
  }
}

export const useGetGlobalSearchResultsPresenter = (
  onClose: () => void
): IUseGetGlobalSearchResultsPresenterResult => {
  const [query, setQuery] = React.useState('')
  const [submittedQuery, setSubmittedQuery] = React.useState('')
  const [selectedTab, setSelectedTab] = React.useState<GlobalSearchTabTypeEnum>(
    GlobalSearchTabTypes.ALL
  )
  const inputRef = React.useRef<HTMLInputElement>(null)

  const { navigate } = useGlobalSearchNavigation()
  const { recentItems } = useQueryGlobalSearchRecentItems()
  const { addRecentItem } = useMutateAddGlobalSearchRecentItem()
  const {
    data: searchResult,
    isLoading: isSearchLoading,
    isError: isSearchError,
  } = useQueryGlobalSearchResults(submittedQuery)

  const showEnterHint = query !== submittedQuery && query.length > 0

  const contentState: Nullable<ContentStateEnum> = deriveContentState({
    hasSubmittedQuery: submittedQuery !== '',
    hasRecentItems: recentItems.length > 0,
    isSearchLoading,
    hasResultItems: (searchResult?.items.length ?? 0) > 0,
    isSearchSuccess:
      !isSearchLoading && !isSearchError && searchResult !== undefined,
    isUnsupportedQuery: searchResult?.isUnsupportedQuery ?? false,
    isSearchError,
  })

  const tabs = React.useMemo(
    () => deriveTabs(searchResult ?? null),
    [searchResult]
  )

  const filteredResultItems = React.useMemo(() => {
    if (!searchResult) return []
    if (selectedTab === GlobalSearchTabTypes.ALL) return searchResult.items
    return searchResult.items.filter((item) => item.type === selectedTab)
  }, [searchResult, selectedTab])

  const showTruncatedNotice = searchResult?.isTruncated ?? false

  const onQueryChange = React.useCallback((value: string) => {
    setQuery(value)
    if (value === '') {
      setSubmittedQuery('')
    }
  }, [])

  const onSubmit = React.useCallback(() => {
    setSubmittedQuery((prev) => (prev === query ? prev : query))
    setSelectedTab(GlobalSearchTabTypes.ALL)
  }, [query])

  const onRecentItemClick = React.useCallback(
    (item: GlobalSearchRecentItem) => {
      const status = 'status' in item.metadata ? item.metadata.status : null
      addRecentItem({
        type: item.type,
        uuid: item.uuid,
        id: item.id,
        metadata: item.metadata,
      })
      onClose()
      navigate({ type: item.type, uuid: item.uuid, id: item.id, status })
    },
    [navigate, addRecentItem, onClose]
  )

  const onResultItemClick = React.useCallback(
    (item: GlobalSearchResultItem) => {
      const status = 'status' in item.metadata ? item.metadata.status : null
      addRecentItem({
        type: item.type,
        uuid: item.uuid,
        id: item.id,
        metadata: item.metadata,
      })
      onClose()
      navigate({ type: item.type, uuid: item.uuid, id: item.id, status })
    },
    [navigate, addRecentItem, onClose]
  )

  const onTabChange = React.useCallback((type: GlobalSearchTabTypeEnum) => {
    setSelectedTab(type)
  }, [])

  return {
    commands: {
      onQueryChange,
      onSubmit,
      onRecentItemClick,
      onResultItemClick,
      onTabChange,
    },
    data: {
      query,
      recentItems,
      resultItems: filteredResultItems,
      tabs,
      inputRef,
    },
    states: {
      showEnterHint,
      contentState,
      selectedTab,
      showTruncatedNotice,
    },
  }
}
